/**
 * QaPage.tsx
 *
 * Quality Assurance case management with:
 * - Status pill tabs (All, In Process, Unable to Resolve, Resolved)
 * - Sidebar filters: Date, Proc Date, Reason, Doctor + full text search
 * - Clickable patient names → patient detail
 * - Clickable reason → modal with full QA details
 * - Email response with AI-assisted composition
 * - Columns: ID, Name, Proc Date, Date Added, Doctor, Reason, Plan
 */
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Download } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PatientLink } from '@/components/ui/PatientLink';
import { cn } from '@/lib/utils';
import { buildStaffOptions, staffQueryKey } from '@/lib/staffOptions';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import type { BadgeVariant } from '@/types/common.types';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import { isDateRange, isSelect } from '@/types/filters.types';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import * as qaSvc from '@/services/qa.service';
import type { QaCase, QaStatus } from '@/services/qa.service';
import * as adminSvc from '@/services/admin.service';
import { QaCaseEditModal } from '@/components/qa/QaCaseEditModal';
import { QaCaseEditor } from '@/components/qa/QaCaseEditor';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { useAuth } from '@/hooks/useAuth';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPatientName(row: QaCase): string {
  return row.patientName?.trim() || '\u2014';
}

// ─── Constants ───────────────────────────────────────────────────────────────
//
// Legacy QA statuses (0-3) from html/lib/patient-details/comm-log-qa.php;
// V5 now mirrors those labels and semantics exactly.

const STATUS_BADGE_MAP: Record<QaStatus, BadgeVariant> = {
  notInProcess:    'neutral',
  inProcess:       'warning',
  unableToResolve: 'error',
  resolved:        'success',
};

const STATUS_LABEL: Record<QaStatus, string> = {
  notInProcess:    'Not in process',
  inProcess:       'In process',
  unableToResolve: 'Unable to resolve',
  resolved:        'Resolved',
};

type StatusTabKey = '' | QaStatus;

const STATUS_TABS: Array<{ key: StatusTabKey; label: string }> = [
  { key: '',                label: 'All Entries' },
  { key: 'notInProcess',    label: STATUS_LABEL.notInProcess },
  { key: 'inProcess',       label: STATUS_LABEL.inProcess },
  { key: 'unableToResolve', label: STATUS_LABEL.unableToResolve },
  { key: 'resolved',        label: STATUS_LABEL.resolved },
];

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search QA cases...' },
  { key: 'daterange', label: 'Date Added', type: 'daterange' },
  { key: 'procdaterange', label: 'Procedure Date', type: 'daterange' },
  { key: 'reason', label: 'Reason', type: 'select', allLabel: 'All reasons', multi: true },
  { key: 'doctor', label: 'Doctor', type: 'select', allLabel: 'All doctors', multi: true },
  { key: 'assignee', label: 'Assignee', type: 'select', allLabel: 'All assignees', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  // Legacy zero-date sentinel means "no date set".
  if (dateStr.startsWith('0000-00-00')) return '\u2014';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function QaPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canSeeList = hasPermission(1);
  const canSubmit = hasPermission(24);

  // Filters — persisted to localStorage via the shared hook
  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'qa');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const [activeTab, setActiveTab] = useLocalStorage<StatusTabKey>('ftp-qa-tab', '');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Detail surfaces.
  //   panelCaseId  → the case shown in the right-side panel (row click).
  //   modalState   → the large fixed-height modal: { caseId: null } = create,
  //                  { caseId: number } = expanded edit. null = closed.
  const [panelCaseId, setPanelCaseId] = useState<number | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const [modalState, setModalState] = useState<{ caseId: number | null } | null>(null);

  function openCasePanel(id: number) {
    setPanelCaseId(id);
    setPanelExpandTrigger((n) => n + 1);
  }

  // ── Lookup data ─────────────────────────────────────────────────────────
  const { data: qaReasons = [] } = useQuery({ queryKey: ['qa-reasons'], queryFn: () => adminSvc.listQaReasons() });
  // This is a FILTER, not an assignment selector: it lists everyone who ever held
  // the role, de-activated people included and labelled. Narrowing it to active
  // staff would make the QA cases of a departed physician unfindable. Legacy does
  // the same for its report filters (admin_report_list.php).
  const { data: doctors = [] } = useQuery({
    queryKey: staffQueryKey('doctors', [], 'all'),
    queryFn: () => listDoctors({ includeInactive: true }),
  });
  const { data: facilities = [] } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60_000,
  });
  const { data: assigneeUsers = [] } = useQuery({ queryKey: ['admin-users'], queryFn: () => adminSvc.listUsers() });
  const { data: assigneeRoles = [] } = useQuery({ queryKey: ['account-roles'], queryFn: () => adminSvc.listAccountRoles() });

  const doctorFilterOptions = useMemo(
    () => buildStaffOptions(doctors, (d) => d.doctorID, (d) => formatDoctorName(d) || `#${d.doctorID}`),
    [doctors],
  );
  const reasonFilterOptions = useMemo(() => qaReasons.map((r) => ({ value: r.qaReasonId, label: r.title })), [qaReasons]);
  const facilityFilterOptions = useMemo<FilterOption[]>(
    () => facilities.filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName || 'Unknown' })),
    [facilities],
  );
  // The "Doctor" filter and column name a role the account may have renamed.
  // FILTER_CONFIGS itself stays static — it keys the persisted filter state —
  // so only the displayed labels are swapped.
  const { roles: accountRoleTitles } = useRoleTitles();
  const doctorLabel = accountRoleTitles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const filterConfigs = useMemo<FilterConfig[]>(
    () => FILTER_CONFIGS.map((f) => (
      f.key === 'doctor'
        ? { ...f, label: doctorLabel, allLabel: `All ${doctorLabel.toLowerCase()}s` }
        : f
    )),
    [doctorLabel],
  );

  const assigneeFilterOptions = useMemo<FilterOption[]>(() => {
    const groupOpts = assigneeRoles.map((r) => ({ value: `group:${r.roleId}`, label: `Group — ${r.roleTitle}` }));
    const userOpts = assigneeUsers
      .filter((u) => u.active)
      .map((u) => ({
        value: `user:${u.userId}`,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || `User #${u.userId}`,
      }));
    return [...groupOpts, ...userOpts];
  }, [assigneeRoles, assigneeUsers]);

  // ── Sidebar filter plumbing ─────────────────────────────────────────────
  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    reason: reasonFilterOptions.map((item) => ({ value: String(item.value), label: item.label })),
    doctor: doctorFilterOptions.map((item) => ({ value: String(item.value), label: item.label })),
    assignee: assigneeFilterOptions,
    facility: facilityFilterOptions,
  }), [doctorFilterOptions, reasonFilterOptions, assigneeFilterOptions, facilityFilterOptions]);

  const handleFilterChange = useCallback((key: string, value: FilterValue) => {
    setPage(1);
    filterState.setFilter(key, value);
  }, [filterState.setFilter]);

  const handleFilterClear = useCallback((key: string) => {
    setPage(1);
    filterState.clearFilter(key);
  }, [filterState.clearFilter]);

  // ── Queries ─────────────────────────────────────────────────────────────
  const params = useMemo(() => {
    const v = effectiveValues;
    const searchVal = isSelect(v.search) ? v.search[0]?.trim() : '';
    const dateRange = isDateRange(v.daterange) ? v.daterange : { from: '', to: '' };
    const procRange = isDateRange(v.procdaterange) ? v.procdaterange : { from: '', to: '' };
    const doctorIds = isSelect(v.doctor) && v.doctor.length > 0 ? v.doctor.map(Number).filter(Boolean) : undefined;
    const reasonIds = isSelect(v.reason) && v.reason.length > 0 ? v.reason.map(Number).filter(Boolean) : undefined;
    const assignees = isSelect(v.assignee) && v.assignee.length > 0 ? v.assignee : undefined;
    const facilityIds = isSelect(v.facility) && v.facility.length > 0 ? v.facility.map(Number).filter(Boolean) : undefined;

    return {
      search: searchVal || undefined,
      qaStatus: activeTab || undefined,
      doctorId: doctorIds && doctorIds.length === 1 ? doctorIds[0] : undefined,
      doctorIds: doctorIds && doctorIds.length > 1 ? doctorIds : undefined,
      reasonId: reasonIds && reasonIds.length === 1 ? reasonIds[0] : undefined,
      reasonIds: reasonIds && reasonIds.length > 1 ? reasonIds : undefined,
      assignee: assignees && assignees.length === 1 ? assignees[0] : undefined,
      assignees: assignees && assignees.length > 1 ? assignees : undefined,
      facilityId: facilityIds && facilityIds.length === 1 ? facilityIds[0] : undefined,
      facilityIds: facilityIds && facilityIds.length > 1 ? facilityIds : undefined,
      dateFrom: dateRange.from || undefined,
      dateTo: dateRange.to || undefined,
      procDateFrom: procRange.from || undefined,
      procDateTo: procRange.to || undefined,
      page,
      pageSize,
    };
  }, [effectiveValues, activeTab, page, pageSize]);

  const { data, isLoading } = useQuery({
    queryKey: ['qa-cases', params],
    queryFn: () => qaSvc.listQaCases(params as qaSvc.QaFilters),
    placeholderData: (prev) => prev,
  });

  // Counts per status for metric cards + tab badges.
  const { data: cntNotInProcess } = useQuery({
    queryKey: ['qa-cases-count', 'notInProcess'],
    queryFn: () => qaSvc.listQaCases({ qaStatus: 'notInProcess', pageSize: 1 }),
  });
  const { data: cntInProcess } = useQuery({
    queryKey: ['qa-cases-count', 'inProcess'],
    queryFn: () => qaSvc.listQaCases({ qaStatus: 'inProcess', pageSize: 1 }),
  });
  const { data: cntUnableToResolve } = useQuery({
    queryKey: ['qa-cases-count', 'unableToResolve'],
    queryFn: () => qaSvc.listQaCases({ qaStatus: 'unableToResolve', pageSize: 1 }),
  });
  const { data: cntResolved } = useQuery({
    queryKey: ['qa-cases-count', 'resolved'],
    queryFn: () => qaSvc.listQaCases({ qaStatus: 'resolved', pageSize: 1 }),
  });

  const counts: Record<StatusTabKey, number> = {
    notInProcess:    cntNotInProcess?.meta?.total ?? 0,
    inProcess:       cntInProcess?.meta?.total ?? 0,
    unableToResolve: cntUnableToResolve?.meta?.total ?? 0,
    resolved:        cntResolved?.meta?.total ?? 0,
    '':
      (cntNotInProcess?.meta?.total ?? 0)
      + (cntInProcess?.meta?.total ?? 0)
      + (cntUnableToResolve?.meta?.total ?? 0)
      + (cntResolved?.meta?.total ?? 0),
  };

  const handleSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['qa-cases'] });
    queryClient.invalidateQueries({ queryKey: ['qa-cases-count'] });
  }, [queryClient]);

  // ── Table ───────────────────────────────────────────────────────────────
  function reasonLabel(row: QaCase): string {
    if (row.reasons && row.reasons.length > 0) return row.reasons.map((r) => r.title).join(', ');
    const ids = [row.qaReason1, row.qaReason2, row.qaReason3, row.qaReason4, row.qaReason5].filter(Boolean);
    if (ids.length === 0) return '\u2014';
    return ids.map((id) => qaReasons.find((qr) => qr.qaReasonId === id)?.title ?? `#${id}`).join(', ');
  }

  const columns: DataTableColumn<QaCase>[] = [
    {
      key: 'patientsQaID',
      header: 'ID',
      render: (row) => <span className="text-xs font-mono text-slate-500">#{row.patientsQaID}</span>,
    },
    {
      key: 'patientName',
      header: 'Name',
      render: (row) =>
        row.patientID ? (
          <PatientLink patientId={row.patientID} patientName={getPatientName(row)} className="font-medium text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>
            {getPatientName(row)}
          </PatientLink>
        ) : (
          <span className="text-slate-500">{getPatientName(row)}</span>
        ),
    },
    {
      key: 'procDate',
      header: 'Proc. Date',
      render: (row) => <span className="text-sm text-slate-600">{fmtDate(row.procDate)}</span>,
    },
    {
      key: 'addDate',
      header: 'Date Added',
      render: (row) => <span className="text-sm text-slate-600">{fmtDate(row.addDate)}</span>,
    },
    {
      key: 'doctorName',
      header: doctorLabel,
      render: (row) => <span className="text-sm text-slate-600">{row.doctorName ?? '\u2014'}</span>,
    },
    {
      key: 'qaStatus',
      header: 'Status',
      render: (row) => {
        const key = (row.qaStatus ?? 'notInProcess') as QaStatus;
        return (
          <Badge variant={STATUS_BADGE_MAP[key] ?? 'neutral'} size="sm">
            {STATUS_LABEL[key] ?? key}
          </Badge>
        );
      },
    },
    {
      key: 'qaReason',
      header: 'Reason',
      render: (row) => (
        <span className="block max-w-[220px] truncate text-sm text-slate-600" title={reasonLabel(row)}>
          {reasonLabel(row)}
        </span>
      ),
    },
    {
      key: 'planTypeName',
      header: 'Plan',
      render: (row) => (
        <span className="text-sm text-slate-600">{row.planTypeName ?? '\u2014'}</span>
      ),
    },
    {
      key: 'facilityName',
      header: 'Facility',
      render: (row) => (
        <span className="text-sm text-slate-600">{row.facilityName ?? '\u2014'}</span>
      ),
    },
  ];

  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'qa', entityLabel: 'QA cases' });

  // ── Export ──────────────────────────────────────────────────────────────
  function handleExportCsv() {
    startExport(params);
  }

  return (
    <>
      <ListPageLayout
        filterSidebar={
          <FilterSidebar
            filters={filterConfigs}
            values={effectiveValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={handleFilterChange}
            onNegateChange={filterState.setNegation}
            onClear={handleFilterClear}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="qa"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        }
        rightPanel={
          panelCaseId !== null ? (
            <DetailRightPanel expandTrigger={panelExpandTrigger} width="w-[30rem]">
              <QaCaseEditor
                caseId={panelCaseId}
                onExpand={() => setModalState({ caseId: panelCaseId })}
                onClose={() => setPanelCaseId(null)}
                onSaved={handleSaved}
              />
            </DetailRightPanel>
          ) : undefined
        }
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-5 p-6">
            <PageHeader
              variant="compact"
              title="Quality Assurance"
              actions={
                <div className="flex items-center gap-2">
                  {canSeeList && (
                    <Button variant="secondary" onClick={handleExportCsv} disabled={!data?.data?.length || exporting} loading={exporting}>
                      <Download className="h-4 w-4 mr-1" /> Export
                    </Button>
                  )}
                  {exportModal}
                  {canSubmit && (
                    <Button onClick={() => setModalState({ caseId: null })}>
                      <Plus className="h-4 w-4 mr-1" /> New Q/A Case
                    </Button>
                  )}
                </div>
              }
            />

            {canSeeList && (
              <>
                <MetricCardGrid columns={4}>
                  <StatCard label={STATUS_LABEL.notInProcess}    value={counts.notInProcess}    tone="slate" />
                  <StatCard label={STATUS_LABEL.inProcess}       value={counts.inProcess}       tone="blue" />
                  <StatCard label={STATUS_LABEL.unableToResolve} value={counts.unableToResolve} tone="amber" />
                  <StatCard label={STATUS_LABEL.resolved}        value={counts.resolved}        tone="green" />
                </MetricCardGrid>

                {/* Status pill tabs */}
                <div className="flex gap-2 flex-wrap">
                  {STATUS_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => { setActiveTab(tab.key); setPage(1); }}
                      className={cn(
                        'px-3.5 py-1.5 text-sm font-medium rounded-full border transition-colors',
                        activeTab === tab.key
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300',
                      )}
                    >
                      {tab.label}
                      <span className={cn(
                        'ml-1.5 text-xs font-bold',
                        activeTab === tab.key ? 'text-indigo-200' : 'text-slate-400',
                      )}>
                        {counts[tab.key] ?? 0}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {!canSeeList && (
              <p className="text-sm text-slate-500">
                You don't have permission to view Q/A cases. Ask an admin to grant the Q/A Visibility permission.
              </p>
            )}
          </div>

          {canSeeList && (
            <div className="min-h-0 flex-1 overflow-auto">
              <DataTable<QaCase>
                data={data?.data ?? []}
                columns={columns}
                keyField="patientsQaID"
                loading={isLoading}
                onRowClick={(row) => openCasePanel(row.patientsQaID)}
                page={page}
                pageSize={pageSize}
                total={data?.meta?.total}
                onPageChange={setPage}
                emptyTitle="No QA cases"
                emptyDescription="Create a new Q/A case to start tracking quality assurance issues."
              />
            </div>
          )}
        </div>
      </ListPageLayout>

      <QaCaseEditModal
        caseId={modalState?.caseId ?? null}
        open={modalState !== null}
        onOpenChange={(o) => setModalState(o ? modalState : null)}
        onSaved={(id) => {
          handleSaved();
          // After creating from the modal, surface the new case in the panel.
          if (modalState?.caseId === null) openCasePanel(id);
        }}
      />
    </>
  );
}
